import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { IonContent, IonPage, IonSpinner, IonModal, IonButton, IonList, IonItem, IonLabel, IonHeader, IonToolbar, IonTitle, useIonViewWillEnter, useIonViewWillLeave } from '@ionic/react';
import { CourseStatusAndNav, Lesson } from '@/components/course';
import { courseService } from '@/services/courseService';
import { userCourseService } from '@/services/userCourseService';
import { Course } from '@/types/course';
import { UserCourse, LessonType } from '@/types/userCourse';
import { Certification } from '@/types/certification';
import { useProfileStore } from '@/store/useProfileStore';
import { showErrorModal } from '@/services/errorModalService';
import { useUserCourseContext } from '@/contexts/UserCourseContext';

interface CourseWithCertification {
  course: Course;
  certificationId: string;
  certificationName: string;
  hasUserCourse: boolean;
}

type CurrentLesson = {
  type: 'video' | 'quiz' | 'game';
  order?: number; // Only for videos
} | null;

export const CoursePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const history = useHistory();
  const [course, setCourse] = useState<Course | null>(null);
  const [certification, setCertification] = useState<Certification | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showCertificationModal, setShowCertificationModal] = useState(false);
  const [courseOptions, setCourseOptions] = useState<CourseWithCertification[]>([]);
  const [quiz, setQuiz] = useState<any>(null);
  const [game, setGame] = useState<any>(null);
  const { activeProfile } = useProfileStore();

  // Get userCourse context
  const { userCourse, setUserCourse } = useUserCourseContext();

  // Local state for currentLesson
  const [currentLesson, setCurrentLesson] = useState<CurrentLesson>(null);

  // Track which course has been initialized (used by currentLesson sync + slug reset)
  const courseIdInitedRef = useRef<string | null>(null);

  // Ionic caches page components in the stack. When this page becomes inactive,
  // clear shared context so stale data isn't visible. When it becomes active again,
  // bump a counter to force fetchUserCourse to re-run (its deps otherwise unchanged).
  const [viewEntryCount, setViewEntryCount] = useState(0);

  useIonViewWillLeave(() => {
    setUserCourse(null);
  });

  useIonViewWillEnter(() => {
    setViewEntryCount(c => c + 1);
  });

  // Get certificationId from URL parameter if provided
  const searchParams = new URLSearchParams(location.search);
  const urlCertificationId = searchParams.get('certificationId');

  // Reset all state when navigating to a different course (same route, different slug).
  // This forces LessonVideo to unmount (via loading spinner) before new data arrives,
  // preventing stale closures from saving progress to the wrong course.
  useEffect(() => {
    setCourse(null);
    setCertification(null);
    setUserCourse(null);
    setCurrentLesson(null);
    setQuiz(null);
    setGame(null);
    setLoading(true);
    courseIdInitedRef.current = null;
  }, [slug]);

  useEffect(() => {
    const fetchCourse = async () => {
      if (!slug || !activeProfile) return;
      try {
        setLoading(true);

        // Fetch data in parallel
        const [certifications, userCertifications] = await Promise.all([
          courseService.getCertificationsWithCourses(),
          courseService.getUserCertifications(activeProfile.id)
        ]);

        // Find all instances of the course across certifications
        const matchingCourses: CourseWithCertification[] = [];

        certifications.forEach((cert: Certification) => {
          cert.courseCategories.forEach(category => {
            category.courses.forEach(course => {
              if (course.slug === slug) {
                matchingCourses.push({
                  course: { ...course, certificationId: cert.id },
                  certificationId: cert.id,
                  certificationName: cert.name,
                  hasUserCourse: course.progress > 0 || course.lessonsCompleted > 0
                });
              }
            });
          });
        });

        if (matchingCourses.length === 0) {
          showErrorModal(
            'Course not found. You will be redirected back.',
            undefined,
            () => history.goBack()
          );
          setLoading(false);
          return;
        }

        // Helper to set course and certification
        const selectCourse = (selectedCourse: CourseWithCertification) => {
          setCourse(selectedCourse.course);
          const cert = certifications.find(c => c.id === selectedCourse.certificationId);
          if (cert) {
            setCertification(cert);
          }
        };

        // Step 1: If certificationId provided in URL, use that
        if (urlCertificationId) {
          const selectedCourse = matchingCourses.find(c => c.certificationId === urlCertificationId);
          if (selectedCourse) {
            selectCourse(selectedCourse);
            setLoading(false);
            return;
          }
        }

        // Step 2: If course only in ONE certification, use it
        if (matchingCourses.length === 1) {
          selectCourse(matchingCourses[0]);
          setLoading(false);
          return;
        }

        // Step 3: Course in MULTIPLE certifications - filter to user's certifications
        const userCertIds = new Set(userCertifications.map(uc => uc.certificationId));
        const userMatchingCourses = matchingCourses.filter(mc => userCertIds.has(mc.certificationId));

        // Step 3a: If user has only ONE of these certifications, use it
        if (userMatchingCourses.length === 1) {
          selectCourse(userMatchingCourses[0]);
          setLoading(false);
          return;
        }

        // Step 3b: User has MULTIPLE certifications OR none - show modal
        // Sort by certification updatedAt (newest first)
        const optionsToShow = userMatchingCourses.length > 0 ? userMatchingCourses : matchingCourses;
        optionsToShow.sort((a, b) => {
          const certA = certifications.find(c => c.id === a.certificationId);
          const certB = certifications.find(c => c.id === b.certificationId);
          if (!certA || !certB) return 0;
          return new Date(certB.updatedAt).getTime() - new Date(certA.updatedAt).getTime();
        });

        setCourseOptions(optionsToShow);
        setShowCertificationModal(true);
        setLoading(false);
      } catch (error) {
        // Error modal already shown by apiService
        setLoading(false);
      }
    };

    fetchCourse();
  }, [slug, activeProfile, urlCertificationId]);

  // Handler for selecting a certification from modal
  const handleCertificationSelect = async (selectedCourse: CourseWithCertification) => {
    setCourse(selectedCourse.course);
    setShowCertificationModal(false);

    // Fetch certification data
    try {
      const certifications = await courseService.getCertificationsWithCourses();
      const cert = certifications.find(c => c.id === selectedCourse.certificationId);
      if (cert) {
        setCertification(cert);
      }
    } catch (error) {
      // Error modal already shown by apiService
    }
  };

  // Fetch user course progress
  useEffect(() => {
    let isCurrent = true;

    const fetchUserCourse = async () => {
      if (!course || !activeProfile) return;

      // Validate certificationId exists
      if (!course.certificationId) {
        showErrorModal(
          'Certification ID missing. Cannot load course. You will be redirected back.',
          undefined,
          () => history.goBack()
        );
        return;
      }

      try {
        const data = await userCourseService.getUserCourse(
          course.certificationId,
          course.id,
          activeProfile.id
        );

        // If slug changed while we were fetching, discard this stale result
        if (!isCurrent) return;

        if (!data) {
          // UserCourse not found - critical error, cannot proceed
          showErrorModal(
            'Failed to load course progress. You will be redirected back.',
            undefined,
            () => history.goBack()
          );
          return;
        }

        setUserCourse(data);
      } catch (error) {
        if (!isCurrent) return;
        // Error modal already shown by apiService, but we need to navigate back
        setUserCourse(null);
        showErrorModal(
          'Failed to load course progress. You will be redirected back.',
          undefined,
          () => history.goBack()
        );
      }
    };

    fetchUserCourse();
    return () => { isCurrent = false; };
  }, [course, activeProfile, setUserCourse, history, viewEntryCount]);

  // Fetch quiz and game data for test panel
  useEffect(() => {
    const fetchLessonData = async () => {
      if (!course) return;

      try {
        // Fetch quiz if course has one
        if (course.quizId) {
          const quizData = await courseService.getQuiz(course.id);
          setQuiz(quizData);
        }

        // Fetch game if course has one
        if (course.gameId) {
          const gameData = await courseService.getGame(course.id);
          setGame(gameData);
        }
      } catch (error) {
        // Silently fail - test panel is optional
      }
    };

    fetchLessonData();
  }, [course]);

  // Initialize currentLesson from userCourse only on first load or when course changes.
  // Do not overwrite when userCourse updates from progress saves (e.g. video progress), or sidebar selection reverts to step 1.

  useEffect(() => {
    if (!userCourse || !course) return;

    const courseChanged = courseIdInitedRef.current !== course.id;
    courseIdInitedRef.current = course.id;

    // Only sync from userCourse when we haven't picked a lesson yet, or when we switched to a different course
    if (courseChanged || currentLesson === null) {
      if (userCourse.lastAccessedLesson && userCourse.lastAccessedLessonId) {
        if (userCourse.lastAccessedLesson === LessonType.VIDEO) {
          const video = course.videos?.find(v => {
            const videoLessonId = `00000000-0000-0000-0000-${v.order.toString().padStart(12, '0')}`;
            return videoLessonId === userCourse.lastAccessedLessonId;
          });
          setCurrentLesson({ type: 'video', order: video?.order || 1 });
        } else {
          setCurrentLesson({ type: userCourse.lastAccessedLesson });
        }
      } else {
        setCurrentLesson(null);
      }
    }
  }, [userCourse, course, currentLesson]);

  // Cleanup: Clear userCourse when navigating away from course page
  useEffect(() => {
    return () => {
      setUserCourse(null);
    };
  }, [setUserCourse]);

  // Navigation callback for lesson changes
  const handleNavigateToLesson = useCallback((lesson: CurrentLesson) => {
    setCurrentLesson(lesson);
  }, []);

  if (loading) {
    return (
      <IonPage>
        <IonContent className="bg-gray-50">
          <div className="flex h-full items-center justify-center">
            <IonSpinner name="crescent" />
          </div>
        </IonContent>
      </IonPage>
    );
  }

  // Show certification selection modal if needed
  if (showCertificationModal) {
    return (
      <IonPage>
        <IonContent className="bg-gray-50">
          <IonModal isOpen={showCertificationModal} onDidDismiss={() => setShowCertificationModal(false)}>
            <IonHeader>
              <IonToolbar>
                <IonTitle>Select Certification</IonTitle>
              </IonToolbar>
            </IonHeader>
            <IonContent className="ion-padding">
              <p className="mb-4">
                This course is available in multiple certifications. Please select which certification you'd like to take this course for:
              </p>
              <IonList>
                {courseOptions.map((option) => (
                  <IonItem
                    key={option.certificationId}
                    button
                    onClick={() => handleCertificationSelect(option)}
                  >
                    <IonLabel>
                      <h2>{option.certificationName}</h2>
                      {option.hasUserCourse && (
                        <p className="text-sm text-green-600">In Progress - {option.course.progress}% complete</p>
                      )}
                    </IonLabel>
                  </IonItem>
                ))}
              </IonList>
              <IonButton
                expand="block"
                fill="outline"
                onClick={() => setShowCertificationModal(false)}
                className="mt-4"
              >
                Cancel
              </IonButton>
            </IonContent>
          </IonModal>
        </IonContent>
      </IonPage>
    );
  }

  if (!course) {
    return (
      <IonPage>
        <IonContent className="bg-gray-50">
          <div className="flex h-full items-center justify-center">
            <p>Course not found</p>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonContent className="bg-gray-50">
        <div className="flex h-full">
          {userCourse && (
            <CourseStatusAndNav
              slug={slug}
              userCourse={userCourse}
              course={course}
              currentLesson={currentLesson}
              onNavigateToLesson={handleNavigateToLesson}
              isOpen={isSidebarOpen}
              onClose={() => setIsSidebarOpen(false)}
              quiz={quiz}
              game={game}
            />
          )}
          <Lesson
            slug={slug}
            course={course}
            userCourse={userCourse}
            currentLesson={currentLesson}
            certification={certification || undefined}
            onUserCourseUpdate={setUserCourse}
            onNavigateToLesson={handleNavigateToLesson}
            onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
          />
        </div>
      </IonContent>
    </IonPage>
  );
};
